import FragmentConfig from "./FragmentConfig.js";

export default class IonGenerationProps {
    /** @type {FragmentConfig[]} */
    #fragments;
    /** @type {number} */
    #qualifierIntensityResolution;
    /** @type {number} */
    #massResolution;
    /**
     * @param {FragmentConfig[]} fragments
     * @param {number} qualifierIntensityResolution
     * @param {number} massResolution
     */
    constructor(fragments = [], qualifierIntensityResolution, massResolution) {
        this.#fragments = fragments;
        this.#qualifierIntensityResolution = qualifierIntensityResolution;
        this.#massResolution = massResolution;
    }

    /** @returns {FragmentConfig[]} */
    get fragments() {
        return this.#fragments;
    }

    /**
     * @param {FragmentConfig} oldFragment
     * @param {FragmentConfig} newFragment
     */
    putFragment(oldFragment, newFragment) {
        const index = this.#fragments.findIndex(f => f.equals(oldFragment));
        if (index === -1)
            this.#fragments.push(newFragment);
        else
            this.#fragments[index] = newFragment;
    }

    /** @param {FragmentConfig} fragment */
    removeFragment(fragment) {
        this.#fragments = this.#fragments.filter(f => !f.equals(fragment) );
    }

    /** @returns {number} */
    get qualifierIntensityResolution() {
        return this.#qualifierIntensityResolution;
    }

    /** @param {number} value */
    set qualifierIntensityResolution(value) {
        this.#qualifierIntensityResolution = value;
    }

    /** @returns {number} */
    get massResolution() {
        return this.#massResolution;
    }

    /** @param {number} value */
    set massResolution(value) {
        this.#massResolution = value;
    }

    /**
     * @param {string} nameWithEffectOnAnalyte
     * @returns {FragmentConfig}
     */
    getFragmentByNameWithEffectOnAnalyte(nameWithEffectOnAnalyte) {
        return this.#fragments.find(f => f.nameWithEffectOnAnalyte === nameWithEffectOnAnalyte);
    }

    /** @returns {string[]} */
    getFragmentNames() {
        return this.#fragments.map(f => f.nameWithEffectOnAnalyte);
    }

    /** @returns {IonGenerationProps} */
    copy() {
        return new IonGenerationProps(
            this.#fragments.map(f => f.copy()),
            this.#qualifierIntensityResolution,
            this.#massResolution
        );
    }

    /**
     * @param {IonGenerationProps} other
     * @returns {boolean}
     */
    equals(other) {
        if (this === other) return true;
        if (!(other instanceof IonGenerationProps)) return false;
        return this.#qualifierIntensityResolution === other.#qualifierIntensityResolution
            && this.#massResolution === other.#massResolution
            && this.#fragments.length === other.#fragments.length
            && this.#fragments.every((f, i) => f.equals(other.#fragments[i]));
    }

    toServerJson() {
        return {
            fragments: this.#fragments.map(f => f.toServerJson()),
            qualifierIntensityResolution: this.#qualifierIntensityResolution,
            massResolution: this.#massResolution
        }
    }

    static parseServerJson(j) {
        return new IonGenerationProps(FragmentConfig.parseMany(j.fragments), j.qualifierIntensityResolution,
            j.massResolution);
    }
}